import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { SortableDragHandle, SortableList } from '@tinycld/core/components/SortableList'
import { PB_SERVER_ADDR } from '@tinycld/core/lib/config'
import { captureException } from '@tinycld/core/lib/errors'
import { mutation, useMutation } from '@tinycld/core/lib/mutations'
import { useStore } from '@tinycld/core/lib/pocketbase'
import { enabledStatusFor } from '@tinycld/core/lib/setup/set-package-enabled'
import { useThemeColor } from '@tinycld/core/lib/use-app-theme'
import { Button, ButtonIcon, ButtonText } from '@tinycld/core/ui/button'
import { Dialog } from '@tinycld/core/ui/dialog'
import { Divider } from '@tinycld/core/ui/divider'
import { FormErrorSummary, TextInput, useForm, z, zodResolver } from '@tinycld/core/ui/form'
import { Switch } from '@tinycld/core/ui/switch'
import {
    AlertTriangle,
    CircleCheck,
    CircleX,
    Download,
    Loader,
    Package,
    Pencil,
    Plus,
    ShieldCheck,
    Trash2,
    X,
} from 'lucide-react-native'
import { newRecordId } from 'pbtsdb/core'
import type PocketBase from 'pocketbase'
import { useCallback, useRef, useState } from 'react'
import {
    ActivityIndicator,
    Pressable,
    TextInput as RNTextInput,
    StyleSheet,
    Text,
    View,
} from 'react-native'
import { shouldAdoptRunningJob } from './adopt-running-job'
import { PageHeader, SectionLabel, SlugTag } from './console-ui'
import {
    InstallProgressModal,
    type ProgressAction,
    progressActionFor,
} from './InstallProgressModal'
import { PackageStatusBadge } from './PackageStatusBadge'
import {
    type CompatViolation,
    type PackageVersionInfo,
    usePackageVersions,
} from './use-package-versions'
import { ConfirmChangesModal } from './version-apply'
import {
    buildVersionOptions,
    ChangeFlag,
    type RowDirection,
    RowVersionSelect,
    stagedDirection,
} from './version-controls'

interface PkgRecord {
    id: string
    name: string
    slug: string
    npm_package: string
    version: string
    status: string
    icon: string
    description: string
    nav_order: number
    has_server: boolean
}

const registerSchema = z.object({
    name: z.string().min(1, 'Required'),
    slug: z
        .string()
        .min(1, 'Required')
        .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, numbers, hyphens'),
    npm_package: z.string().min(1, 'Required'),
    description: z.string(),
})

interface PackageManagerProps {
    pb: PocketBase
    // Gates the version-discovery fetch so it only runs when this screen is shown
    // (the dashboard keeps every tab mounted). Defaults true for any standalone use.
    isVisible?: boolean
}

export function PackageManager({ pb, isVisible = true }: PackageManagerProps) {
    const [showRegister, setShowRegister] = useState(false)
    const [showInstall, setShowInstall] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [installJob, setInstallJob] = useState<{ jobId: string; action: ProgressAction } | null>(
        null
    )
    const [confirmOpen, setConfirmOpen] = useState(false)

    // Version discovery + the staging/compat/apply solver. Lives alongside the
    // lifecycle list so a package's version sits on its own row.
    const vm = usePackageVersions(pb, isVisible)
    const versionBySlug = new Map(vm.versions.map(v => [v.slug, v]))

    // The registry list is a live pbtsdb query: pbtsdb subscribes to PocketBase
    // realtime, so client mutations (reorder/toggle/edit/register/delete) AND the
    // server-side build jobs (install/uninstall/version-apply, which write
    // pkg_registry from Go) both propagate here automatically — no manual refetch.
    const [pkgRegistryCollection, pkgInstallLogCollection] = useStore(
        'pkg_registry',
        'pkg_install_log'
    )
    const { data: rows = [], isLoading } = useLiveQuery({
        query: query => query.from({ pkg_registry: pkgRegistryCollection }),
    })
    const packages = [...(rows as PkgRecord[])].sort(
        (a, b) =>
            (a.nav_order ?? 0) - (b.nav_order ?? 0) || (a.name ?? '').localeCompare(b.name ?? '')
    )

    const handleInstallStarted = useCallback((jobId: string) => {
        setShowInstall(false)
        setInstallJob({ jobId, action: 'install' })
    }, [])

    const handleUninstallStarted = useCallback((jobId: string) => {
        setInstallJob({ jobId, action: 'uninstall' })
    }, [])

    // Install/uninstall and apply (upgrade/downgrade) each start a background job,
    // but only ONE runs at a time (server single-flight). Collapse both sources
    // into a single active descriptor so the progress panel renders in ONE place
    // with the handlers appropriate to whichever started it. The package list
    // refreshes itself via pbtsdb realtime, so these callbacks only dismiss the
    // progress panel and refresh the version-discovery view (a separate server
    // query, not a pbtsdb store).
    //
    // Still true under `realtime: 'query'` (pbtsdb 0.10): the running-jobs query
    // below subscribes with its own `status = "running"` filter, and the server
    // emits a DELETE to a subscription whose filter a row has left (PR #296), so
    // a job flipping off `running` drops out of this list live rather than
    // lingering until the next fetch.
    // A job already RUNNING when this screen is opened — started by another
    // admin, or by this one before a page reload — is adopted from the install
    // log, so the panel shows it rather than leaving the screen looking idle
    // while the deployment is mid-rebuild. The SSE stream backfills the steps
    // so far, so a late subscriber sees the same bar. The row's status flips
    // off `running` when the job ends; the panel keeps the job in local state
    // so it can still show the terminal result until it is closed.
    const { data: runningLogs = [] } = useLiveQuery({
        query: query =>
            query
                .from({ log: pkgInstallLogCollection })
                .where(({ log }) => eq(log.status, 'running'))
                .orderBy(({ log }) => log.created, 'desc'),
    })
    // Jobs the admin has already closed. Without this, closing the panel sets
    // installJob to null and the adoption below immediately re-adopts the same
    // job on the very next render — the Close button looks dead. A ref, not
    // state: dismissing must not itself schedule a render.
    const dismissedJobs = useRef<Set<string>>(new Set())

    const runningJob = runningLogs[0]
    if (
        shouldAdoptRunningJob({
            runningJobId: runningJob?.job_id,
            hasPanelJob: installJob !== null,
            hasApplyJob: vm.applyJobId != null,
            isDismissed: id => dismissedJobs.current.has(id),
        })
    ) {
        setInstallJob({ jobId: runningJob.job_id, action: progressActionFor(runningJob.action) })
    }

    const activeProgress = installJob
        ? {
              jobId: installJob.jobId,
              action: installJob.action,
              onClose: () => {
                  dismissedJobs.current.add(installJob.jobId)
                  setInstallJob(null)
              },
              onComplete: () => {},
          }
        : vm.applyJobId
          ? {
                jobId: vm.applyJobId,
                action: 'apply' as const,
                onClose: () => vm.onApplyComplete(),
                onComplete: () => vm.refresh(),
            }
          : null

    return (
        <View className="gap-6">
            <PageHeader
                title="Packages"
                subtitle="Features installed on this deployment. Toggle availability, reorder the sidebar, change versions, install from npm, or register a new source."
                actions={
                    <>
                        <Button onPress={() => setShowRegister(true)} size="sm" variant="outline">
                            <ButtonIcon as={Plus} />
                            <ButtonText>Register</ButtonText>
                        </Button>
                        <Button onPress={() => setShowInstall(true)} size="sm">
                            <ButtonIcon as={Download} />
                            <ButtonText>Install package</ButtonText>
                        </Button>
                    </>
                }
            />

            <InstallPackageModal
                isOpen={showInstall}
                pb={pb}
                onClose={() => setShowInstall(false)}
                onStarted={handleInstallStarted}
            />

            <InstallProgressModal
                isVisible={activeProgress !== null}
                jobId={activeProgress?.jobId ?? null}
                action={activeProgress?.action ?? 'install'}
                authToken={pb.authStore.token}
                onClose={() => activeProgress?.onClose()}
                onComplete={() => activeProgress?.onComplete()}
            />

            <RegisterPackageModal
                isOpen={showRegister}
                onClose={() => setShowRegister(false)}
                onCreated={() => setShowRegister(false)}
            />

            <View className="gap-3">
                <View className="flex-row items-center justify-between">
                    <SectionLabel>Installed · drag to reorder sidebar</SectionLabel>
                    <StageActions
                        pendingCount={vm.pendingChanges.length}
                        onSelectAll={vm.selectAllUpdates}
                        onClear={vm.clearSelection}
                    />
                </View>
                <View className="rounded-xl border overflow-hidden bg-surface-secondary border-border">
                    <PackageList
                        packages={packages}
                        isLoading={isLoading}
                        pb={pb}
                        editingId={editingId}
                        onEdit={setEditingId}
                        onUninstallStarted={handleUninstallStarted}
                        versionBySlug={versionBySlug}
                        targets={vm.targets}
                        onSetTarget={vm.setTarget}
                    />
                    <ViolationList violations={vm.violations} isChecking={vm.isChecking} />
                    <ApplyFooter
                        pendingCount={vm.pendingChanges.length}
                        violationCount={vm.violations.length}
                        isChecking={vm.isChecking}
                        canApply={vm.canApply}
                        onApply={() => setConfirmOpen(true)}
                    />
                </View>
            </View>

            <ConfirmChangesModal
                isOpen={confirmOpen}
                pendingChanges={vm.pendingChanges}
                hasDowngrade={vm.hasDowngrade}
                fetchDropReport={vm.fetchDropReport}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={async () => {
                    await vm.applyChanges()
                    setConfirmOpen(false)
                }}
            />
        </View>
    )
}

function PackageList({
    packages,
    isLoading,
    pb,
    editingId,
    onEdit,
    onUninstallStarted,
    versionBySlug,
    targets,
    onSetTarget,
}: {
    packages: PkgRecord[]
    isLoading: boolean
    pb: PocketBase
    editingId: string | null
    onEdit: (id: string | null) => void
    onUninstallStarted: (jobId: string) => void
    versionBySlug: Map<string, PackageVersionInfo>
    targets: Record<string, string>
    onSetTarget: (slug: string, version: string) => void
}) {
    const surfaceBg = useThemeColor('surface-secondary')
    const accentColor = useThemeColor('accent')
    const borderColor = useThemeColor('border')
    const mutedColor = useThemeColor('muted-foreground')
    const [pkgRegistryCollection] = useStore('pkg_registry')

    const pkgMap = new Map(packages.map(p => [p.id, p]))

    const reorder = useMutation({
        mutationFn: mutation(function* (data: PkgRecord[]) {
            yield data.map((pkg, i) =>
                pkgRegistryCollection.update(pkg.id, draft => {
                    draft.nav_order = i * 10
                })
            )
        }),
        onError: err => captureException('Failed to save package order', err),
    })

    const handleReorder = useCallback((data: PkgRecord[]) => reorder.mutate(data), [reorder])

    function renderItem({ item }: { item: PkgRecord; index: number }) {
        const pkg = pkgMap.get(item.id) ?? item
        const info = versionBySlug.get(pkg.slug)
        const target = targets[pkg.slug]
        // The `core` row is the TinyCld base (app shell + core + server). It
        // upgrades through the same version-change pipeline as any package, so it
        // gets the normal version picker and update-available badge. Only a narrow
        // set of traits stay special: a distinct `base` status badge, no
        // drag/reorder (the base has no nav position), and no lifecycle controls
        // (it can't be disabled or uninstalled).
        const isBase = pkg.slug === 'core'
        // A row with a staged version change locks its lifecycle controls
        // (toggle/drag/uninstall) until the change is applied or cleared, so an
        // immediate mutation can't collide with the pending transaction.
        const isStaged = info ? stagedDirection(info, target) !== 'none' : false

        return (
            <View
                testID={`pkg-row-${pkg.slug}`}
                style={{
                    backgroundColor: isStaged ? `${accentColor}1f` : surfaceBg,
                    borderBottomWidth: StyleSheet.hairlineWidth,
                    borderColor,
                }}
            >
                <View className="flex-row items-center gap-3 px-4 py-3.5">
                    <SortableDragHandle color={mutedColor} testID={`pkg-drag-${pkg.slug}`} />
                    <View className="w-10 h-10 rounded-xl items-center justify-center bg-surface border border-border">
                        <Package size={18} color={mutedColor} />
                    </View>
                    <View className="flex-1 gap-0.5">
                        <View className="flex-row gap-2 items-center">
                            <Text
                                style={{
                                    fontSize: 15,
                                    fontWeight: '600',
                                }}
                                className="text-foreground"
                            >
                                {pkg.name}
                            </Text>
                            <PackageStatusBadge status={isBase ? 'base' : pkg.status} />
                            {info?.hasUpdate && !isStaged ? (
                                <PackageStatusBadge status="update-available" />
                            ) : null}
                        </View>
                        <View className="flex-row gap-2 items-center flex-wrap">
                            <SlugTag>{pkg.slug}</SlugTag>
                            {pkg.description ? (
                                <Text
                                    className="text-muted-foreground"
                                    style={{ fontSize: 13 }}
                                    numberOfLines={1}
                                >
                                    {pkg.description}
                                </Text>
                            ) : null}
                        </View>
                    </View>
                    <RowVersion info={info} target={target} onSetTarget={onSetTarget} />
                    <PackageActions
                        pkg={pkg}
                        pb={pb}
                        isBase={isBase}
                        isEditing={editingId === pkg.id}
                        isLocked={isStaged}
                        onEdit={() => onEdit(editingId === pkg.id ? null : pkg.id)}
                        onUninstallStarted={onUninstallStarted}
                    />
                </View>
                <EditPackageForm
                    isVisible={editingId === pkg.id && !isStaged}
                    pkg={pkg}
                    onClose={() => onEdit(null)}
                />
            </View>
        )
    }

    const keyExtractor = useCallback((item: PkgRecord) => item.id, [])

    if (isLoading) {
        return (
            <View className="p-8 items-center">
                <ActivityIndicator size="large" color={mutedColor} />
            </View>
        )
    }

    if (packages.length === 0) {
        return (
            <View className="p-8 items-center gap-2">
                <Package size={32} color={`${mutedColor}60`} />
                <Text className="text-muted-foreground" style={{ fontSize: 15 }}>
                    No packages installed yet
                </Text>
            </View>
        )
    }

    return (
        <SortableList
            data={packages}
            keyExtractor={keyExtractor}
            onReorder={handleReorder}
            renderItem={renderItem}
        />
    )
}

// RowVersion is the per-row version cell. A package with >1 available version
// gets the interactive picker + change flag; a single-version package
// (unmanaged sources) shows just its static version, no dropdown — there's
// nothing to stage.
function RowVersion({
    info,
    target,
    onSetTarget,
}: {
    info?: PackageVersionInfo
    target?: string
    onSetTarget: (slug: string, version: string) => void
}) {
    if (!info) {
        return null
    }

    const options = buildVersionOptions(info)
    const direction: RowDirection = stagedDirection(info, target)

    if (options.length <= 1) {
        return (
            <View style={{ width: 150 }} className="items-end">
                <Text
                    className="text-muted-foreground"
                    style={{ fontFamily: 'monospace', fontSize: 13 }}
                >
                    v{info.current || '—'}
                </Text>
            </View>
        )
    }

    return (
        <View style={{ width: 150 }} className="gap-1">
            <RowVersionSelect
                options={options}
                value={target ?? info.current}
                direction={direction}
                onChange={v => onSetTarget(info.slug, v)}
            />
            <ChangeFlag direction={direction} />
        </View>
    )
}

function PackageActions({
    pkg,
    pb,
    isBase,
    isEditing,
    isLocked,
    onEdit,
    onUninstallStarted,
}: {
    pkg: PkgRecord
    pb: PocketBase
    // The TinyCld base row: no toggle (can't disable the platform), no uninstall,
    // no metadata edit (its name/description are generator-owned and reset on the
    // next build). Upgraded via its version picker like any package.
    isBase: boolean
    isEditing: boolean
    // True when this package has a staged version change — its toggle and
    // uninstall are disabled until the change is applied or cleared, so an
    // immediate mutation can't race the pending transaction.
    isLocked: boolean
    onEdit: () => void
    onUninstallStarted: (jobId: string) => void
}) {
    const mutedColor = useThemeColor('muted-foreground')
    const dangerColor = useThemeColor('danger')
    const primaryBg = useThemeColor('primary')
    const [showUninstall, setShowUninstall] = useState(false)
    const [pkgRegistryCollection] = useStore('pkg_registry')

    const isEnabled = pkg.status !== 'disabled'
    const isInstalled = pkg.status === 'installed'

    const toggle = useMutation({
        mutationFn: mutation(function* () {
            yield pkgRegistryCollection.update(pkg.id, draft => {
                draft.status = enabledStatusFor(!isEnabled)
            })
        }),
        onError: err => captureException('Failed to toggle package status', err),
    })

    const handleUninstall = async () => {
        const response = await fetch(`${PB_SERVER_ADDR}/api/admin/packages/uninstall`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: pb.authStore.token,
            },
            body: JSON.stringify({ slug: pkg.slug }),
        })
        const data = await response.json()
        if (!response.ok) {
            throw new Error(data.error ?? 'Failed to start uninstall')
        }
        setShowUninstall(false)
        onUninstallStarted(data.jobId)
    }

    // The base row carries no lifecycle controls — it's upgraded via its version
    // picker like any package, just never disabled, edited, or uninstalled.
    if (isBase) {
        return null
    }

    return (
        <View
            className="flex-row gap-2 items-center"
            style={isLocked ? { opacity: 0.5 } : undefined}
        >
            <Switch
                value={isEnabled}
                onValueChange={() => toggle.mutate()}
                disabled={toggle.isPending || isLocked}
            />
            <Pressable
                onPress={onEdit}
                disabled={isLocked}
                className="p-2 rounded-lg"
                style={{ backgroundColor: isEditing ? `${primaryBg}15` : 'transparent' }}
            >
                <Pencil size={15} color={isEditing ? primaryBg : mutedColor} />
            </Pressable>
            {isInstalled ? (
                <Pressable
                    onPress={() => setShowUninstall(true)}
                    disabled={isLocked}
                    className="p-2 rounded-lg"
                >
                    <Trash2 size={15} color={dangerColor} />
                </Pressable>
            ) : null}
            <UninstallModal
                isOpen={showUninstall}
                name={pkg.name}
                slug={pkg.slug}
                onClose={() => setShowUninstall(false)}
                onConfirm={handleUninstall}
            />
        </View>
    )
}

// StageActions: the "stage all updates" / "clear" controls in the section header,
// shown alongside the package list. Mirrors the old Versions tab's selection bar.
function StageActions({
    pendingCount,
    onSelectAll,
    onClear,
}: {
    pendingCount: number
    onSelectAll: () => void
    onClear: () => void
}) {
    return (
        <View className="flex-row gap-2 items-center">
            {pendingCount > 0 && (
                <Button onPress={onClear} size="sm" variant="ghost">
                    <ButtonText>Clear ({pendingCount})</ButtonText>
                </Button>
            )}
            <Button onPress={onSelectAll} size="sm" variant="outline">
                <ButtonText>Stage all updates</ButtonText>
            </Button>
        </View>
    )
}

// ViolationList: the compatibility panel between the rows and the apply footer,
// inside the same bordered list, so a conflict reads as part of the staged set.
function ViolationList({
    violations,
    isChecking,
}: {
    violations: CompatViolation[]
    isChecking: boolean
}) {
    const dangerColor = useThemeColor('danger')
    if (isChecking || violations.length === 0) return null
    return (
        <View className="p-4 gap-2.5 bg-danger-soft border-t border-border">
            {violations.map(v => (
                <View key={`${v.package}:${v.requires}`} className="flex-row gap-2.5 items-start">
                    <AlertTriangle size={16} color={dangerColor} style={{ marginTop: 2 }} />
                    <Text className="text-danger flex-1" style={{ fontSize: 13.5 }}>
                        <Text style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                            {v.package}
                        </Text>{' '}
                        requires{' '}
                        <Text style={{ fontFamily: 'monospace' }}>
                            {v.requires} {v.range}
                        </Text>{' '}
                        — found {v.found || 'not installed'}
                    </Text>
                </View>
            ))}
        </View>
    )
}

// ApplyFooter: the list's status bar — a live compatibility verdict on the left,
// the apply action on the right. Renders only once something is staged.
function ApplyFooter({
    pendingCount,
    violationCount,
    isChecking,
    canApply,
    onApply,
}: {
    pendingCount: number
    violationCount: number
    isChecking: boolean
    canApply: boolean
    onApply: () => void
}) {
    if (pendingCount === 0) return null
    const hasViolation = violationCount > 0
    return (
        <View
            className={`flex-row items-center gap-4 px-4 py-3.5 border-t border-border ${hasViolation ? 'bg-danger-soft' : 'bg-surface'}`}
        >
            <SolverStatus
                isChecking={isChecking}
                violationCount={violationCount}
                pendingCount={pendingCount}
            />
            <View className="flex-1" />
            <Button
                onPress={onApply}
                isDisabled={!canApply}
                size="sm"
                variant={hasViolation ? 'destructive' : 'default'}
            >
                <ButtonText>
                    Apply {pendingCount} change{pendingCount === 1 ? '' : 's'}
                </ButtonText>
            </Button>
        </View>
    )
}

function SolverStatus({
    isChecking,
    violationCount,
    pendingCount,
}: {
    isChecking: boolean
    violationCount: number
    pendingCount: number
}) {
    const infoColor = useThemeColor('link')
    const successColor = useThemeColor('success')
    const dangerColor = useThemeColor('danger')

    if (isChecking) {
        return (
            <View className="flex-row items-center gap-2">
                <Loader size={16} color={infoColor} />
                <Text style={{ color: infoColor, fontSize: 13.5 }}>Checking compatibility…</Text>
            </View>
        )
    }
    if (violationCount > 0) {
        return (
            <View className="flex-row items-center gap-2">
                <CircleX size={16} color={dangerColor} />
                <Text style={{ color: dangerColor, fontSize: 13.5 }}>
                    <Text style={{ fontWeight: '600' }}>
                        {violationCount} conflict{violationCount === 1 ? '' : 's'}
                    </Text>{' '}
                    · {pendingCount} staged
                </Text>
            </View>
        )
    }
    return (
        <View className="flex-row items-center gap-2">
            <CircleCheck size={16} color={successColor} />
            <Text style={{ color: successColor, fontSize: 13.5 }}>
                Compatible · {pendingCount} staged
            </Text>
        </View>
    )
}

// UninstallModal requires the operator to type the package slug before the
// destructive action unlocks — the same typed-confirmation guard used for
// downgrades, so an errant click can't drop a package's data.
function UninstallModal({
    isOpen,
    name,
    slug,
    onClose,
    onConfirm,
}: {
    isOpen: boolean
    name: string
    slug: string
    onClose: () => void
    onConfirm: () => Promise<void>
}) {
    const dangerColor = useThemeColor('danger')
    const fgColor = useThemeColor('foreground')
    const mutedColor = useThemeColor('muted-foreground')
    const successColor = useThemeColor('success')
    const [typed, setTyped] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (!isOpen) return null

    const confirmEnabled = typed.trim() === slug && !submitting

    const close = () => {
        setTyped('')
        setError(null)
        onClose()
    }

    const confirm = async () => {
        setSubmitting(true)
        setError(null)
        try {
            await onConfirm()
            setTyped('')
        } catch (err) {
            captureException('setup.packages.uninstall', err)
            setError(err instanceof Error ? err.message : 'Failed to start uninstall')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog
            isOpen
            onClose={close}
            size="lg"
            title={`Uninstall ${name}`}
            header={<UninstallHeader name={name} />}
        >
            <Dialog.Body>
                <View className="gap-2">
                    <Text className="text-muted-foreground" style={{ fontSize: 13 }}>
                        Type{' '}
                        <Text style={{ fontFamily: 'monospace', color: dangerColor }}>{slug}</Text>{' '}
                        to confirm
                    </Text>
                    <RNTextInput
                        value={typed}
                        onChangeText={setTyped}
                        autoCapitalize="none"
                        placeholder={slug}
                        placeholderTextColor={mutedColor}
                        className="rounded-lg border border-border px-3 py-2.5 bg-surface"
                        style={{ color: fgColor, fontSize: 14, fontFamily: 'monospace' }}
                    />
                </View>

                <View className="flex-row gap-2 items-center px-3 py-2.5 rounded-lg bg-success-soft">
                    <ShieldCheck size={16} color={successColor} />
                    <Text className="text-success-soft-foreground" style={{ fontSize: 13 }}>
                        A backup is taken before uninstalling.
                    </Text>
                </View>
            </Dialog.Body>

            <PinnedDialogError message={error} />

            <Dialog.Footer>
                <Dialog.CancelButton onPress={close} isDisabled={submitting} />
                <Dialog.ActionButton
                    label={submitting ? 'Uninstalling…' : 'Uninstall'}
                    onPress={confirm}
                    isDisabled={!confirmEnabled}
                    isDestructive
                />
            </Dialog.Footer>
        </Dialog>
    )
}

// UninstallHeader replaces the Dialog's default title row so the confirm keeps
// its icon tile beside the title.
function UninstallHeader({ name }: { name: string }) {
    const dangerColor = useThemeColor('danger')
    return (
        <View className="flex-row gap-3 items-start px-5 pt-5 pb-3">
            <View className="w-10 h-10 rounded-xl items-center justify-center bg-danger-soft">
                <Trash2 size={20} color={dangerColor} />
            </View>
            <View className="flex-1 gap-1">
                <Text className="text-foreground" style={{ fontSize: 18, fontWeight: '600' }}>
                    Uninstall {name}
                </Text>
                <Text className="text-muted-foreground" style={{ fontSize: 14 }}>
                    This removes the feature and runs its down-migrations. Records owned by this
                    package will be dropped.
                </Text>
            </View>
        </View>
    )
}

function DialogError({ message }: { message: string | null }) {
    if (!message) return null
    return (
        <View className="rounded-md p-2.5 bg-danger-soft">
            <Text className="text-xs text-danger">{message}</Text>
        </View>
    )
}

// Pinned between the body and the footer so a failure is visible without scrolling.
function PinnedDialogError({ message }: { message: string | null }) {
    if (!message) return null
    return (
        <View className="px-5 pb-4">
            <DialogError message={message} />
        </View>
    )
}

const editSchema = z.object({
    name: z.string().min(1, 'Required'),
    description: z.string(),
    icon: z.string(),
    nav_order: z.string(),
})

function EditPackageForm({
    isVisible,
    pkg,
    onClose,
}: {
    isVisible: boolean
    pkg: PkgRecord
    onClose: () => void
}) {
    const mutedColor = useThemeColor('muted-foreground')
    const dangerColor = useThemeColor('danger')
    const borderColor = useThemeColor('border')
    const [saveError, setSaveError] = useState<string | null>(null)
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [pkgRegistryCollection] = useStore('pkg_registry')

    const remove = useMutation({
        mutationFn: mutation(function* () {
            yield pkgRegistryCollection.delete(pkg.id)
        }),
        onSuccess: () => onClose(),
        onError: err => setSaveError(err instanceof Error ? err.message : 'Failed to remove'),
    })
    const handleRemove = () => remove.mutate()

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitted, isDirty },
    } = useForm({
        resolver: zodResolver(editSchema),
        defaultValues: {
            name: pkg.name,
            description: pkg.description ?? '',
            icon: pkg.icon ?? '',
            nav_order: String(pkg.nav_order ?? 0),
        },
        mode: 'onChange',
    })

    const save = useMutation({
        mutationFn: mutation(function* (data: z.infer<typeof editSchema>) {
            yield pkgRegistryCollection.update(pkg.id, draft => {
                draft.name = data.name
                draft.description = data.description
                draft.icon = data.icon
                draft.nav_order = Number(data.nav_order) || 0
            })
        }),
        onSuccess: () => onClose(),
        onError: err => setSaveError(err instanceof Error ? err.message : 'Failed to update'),
    })

    const onSave = handleSubmit(data => {
        setSaveError(null)
        save.mutate(data)
    })

    if (!isVisible) return null

    const saveEnabled = isDirty && !save.isPending

    return (
        <View
            className="mx-3 mb-3 rounded-xl p-4 gap-3"
            style={{
                backgroundColor: `${borderColor}30`,
                borderWidth: StyleSheet.hairlineWidth,
                borderColor,
            }}
        >
            <View className="flex-row items-center justify-between">
                <SectionLabel>Edit package</SectionLabel>
                <SlugTag>
                    {pkg.slug}
                    {pkg.version ? ` · v${pkg.version}` : ''}
                </SlugTag>
            </View>

            <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
            {saveError && (
                <View className="rounded-md p-2.5 bg-danger-soft">
                    <Text className="text-xs text-danger">{saveError}</Text>
                </View>
            )}

            <View className="flex-row gap-3 flex-wrap">
                <View className="flex-1 min-w-[150px]">
                    <TextInput control={control} name="name" label="Name" />
                </View>
                <View style={{ width: 120 }}>
                    <TextInput control={control} name="icon" label="Icon" />
                </View>
                <View style={{ width: 72 }}>
                    <TextInput control={control} name="nav_order" label="Order" />
                </View>
            </View>
            <TextInput control={control} name="description" label="Description" />

            <Divider />

            <View className="flex-row items-center">
                {confirmRemove ? (
                    <View className="flex-row gap-1.5 items-center">
                        <Button onPress={handleRemove} size="sm" variant="destructive">
                            <ButtonText>Confirm remove</ButtonText>
                        </Button>
                        <Pressable onPress={() => setConfirmRemove(false)} className="p-1.5">
                            <X size={14} color={mutedColor} />
                        </Pressable>
                    </View>
                ) : (
                    <Pressable
                        onPress={() => setConfirmRemove(true)}
                        className="flex-row gap-1 items-center px-2.5 py-1.5 rounded-md"
                    >
                        <Trash2 size={13} color={dangerColor} />
                        <Text className="text-danger" style={{ fontWeight: '500', fontSize: 13 }}>
                            Remove
                        </Text>
                    </Pressable>
                )}
                <View className="flex-1" />
                <View className="flex-row gap-2 items-center">
                    <Pressable onPress={onClose} className="px-3 py-1.5 rounded-md">
                        <Text
                            className="text-muted-foreground"
                            style={{ fontWeight: '500', fontSize: 13 }}
                        >
                            Cancel
                        </Text>
                    </Pressable>
                    <Button onPress={onSave} isDisabled={!saveEnabled} size="sm">
                        <ButtonText>{save.isPending ? 'Saving…' : 'Save'}</ButtonText>
                    </Button>
                </View>
            </View>
        </View>
    )
}

const registerDefaults = { name: '', slug: '', npm_package: '', description: '' }

function RegisterPackageModal({
    isOpen,
    onClose,
    onCreated,
}: {
    isOpen: boolean
    onClose: () => void
    onCreated: () => void
}) {
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [pkgRegistryCollection] = useStore('pkg_registry')

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitted },
    } = useForm({
        resolver: zodResolver(registerSchema),
        defaultValues: registerDefaults,
        mode: 'onChange',
    })

    const close = () => {
        reset(registerDefaults)
        setSubmitError(null)
        onClose()
    }

    const create = useMutation({
        mutationFn: mutation(function* (data: z.infer<typeof registerSchema>) {
            yield pkgRegistryCollection.insert({
                id: newRecordId(),
                name: data.name,
                slug: data.slug,
                npm_package: data.npm_package,
                description: data.description,
                status: 'available',
                nav_order: 0,
                // The install pipeline fills these in when the source is installed;
                // a freshly-registered, never-installed source has no version/icon
                // and no compiled manifest yet.
                version: '',
                icon: '',
                manifest_json: '',
                has_server: false,
            })
        }),
        onSuccess: () => {
            reset(registerDefaults)
            onCreated()
        },
        onError: err => setSubmitError(err instanceof Error ? err.message : 'Failed to register'),
    })

    const onSubmit = handleSubmit(data => {
        setSubmitError(null)
        create.mutate(data)
    })

    if (!isOpen) return null

    return (
        <Dialog isOpen onClose={close} size="xl" title="Register a package source">
            <Dialog.Body>
                <FormErrorSummary errors={errors} isEnabled={isSubmitted} />

                <DialogError message={submitError} />

                <View className="flex-row gap-3 flex-wrap">
                    <View className="flex-1 min-w-[200px]">
                        <TextInput
                            control={control}
                            name="name"
                            label="Display name"
                            placeholder="Contacts"
                        />
                    </View>
                    <View className="flex-1 min-w-[200px]">
                        <TextInput
                            control={control}
                            name="slug"
                            label="Slug"
                            placeholder="my-feature"
                            autoCapitalize="none"
                            hint="kebab-case"
                        />
                    </View>
                </View>
                <View className="flex-row gap-3 flex-wrap">
                    <View className="flex-1 min-w-[200px]">
                        <TextInput
                            control={control}
                            name="npm_package"
                            label="npm package / git URL"
                            placeholder="@tinycld/my-feature"
                            autoCapitalize="none"
                        />
                    </View>
                    <View className="flex-1 min-w-[200px]">
                        <TextInput
                            control={control}
                            name="description"
                            label="Description"
                            placeholder="Optional"
                        />
                    </View>
                </View>
            </Dialog.Body>

            <Dialog.Footer>
                <Dialog.CancelButton onPress={close} isDisabled={create.isPending} />
                <Dialog.ActionButton
                    label={create.isPending ? 'Registering…' : 'Register'}
                    onPress={onSubmit}
                    isDisabled={create.isPending}
                />
            </Dialog.Footer>
        </Dialog>
    )
}

const installSchema = z.object({
    npm_package: z.string().min(1, 'Required'),
})

function InstallPackageModal({
    isOpen,
    pb,
    onClose,
    onStarted,
}: {
    isOpen: boolean
    pb: PocketBase
    onClose: () => void
    onStarted: (jobId: string) => void
}) {
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isInstalling, setIsInstalling] = useState(false)

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitted },
    } = useForm({
        resolver: zodResolver(installSchema),
        defaultValues: { npm_package: '' },
        mode: 'onChange',
    })

    const npmValue = watch('npm_package')
    const showWarning = npmValue.length > 0 && !npmValue.startsWith('@tinycld/')

    const close = () => {
        reset({ npm_package: '' })
        setSubmitError(null)
        onClose()
    }

    const onSubmit = handleSubmit(async data => {
        setSubmitError(null)
        setIsInstalling(true)
        try {
            const response = await fetch(`${PB_SERVER_ADDR}/api/admin/packages/install`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: pb.authStore.token,
                },
                body: JSON.stringify({ npmPackage: data.npm_package }),
            })
            const result = await response.json()
            if (!response.ok) {
                setSubmitError(result.error ?? 'Failed to start install')
                return
            }
            reset({ npm_package: '' })
            onStarted(result.jobId)
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to start install')
        } finally {
            setIsInstalling(false)
        }
    })

    if (!isOpen) return null

    return (
        <Dialog isOpen onClose={close} size="xl" title="Install from npm or git">
            <Dialog.Body>
                <FormErrorSummary errors={errors} isEnabled={isSubmitted} />

                <DialogError message={submitError} />

                <SecurityWarning isVisible={showWarning} />

                <TextInput
                    control={control}
                    name="npm_package"
                    label="Package source"
                    placeholder="@tinycld/my-feature"
                    autoCapitalize="none"
                    hint="npm package name, version spec, or a git URL"
                />
            </Dialog.Body>

            <Dialog.Footer>
                <Dialog.CancelButton onPress={close} isDisabled={isInstalling} />
                <Dialog.ActionButton
                    label={isInstalling ? 'Starting…' : 'Install'}
                    onPress={onSubmit}
                    isDisabled={isInstalling}
                />
            </Dialog.Footer>
        </Dialog>
    )
}

function SecurityWarning({ isVisible }: { isVisible: boolean }) {
    const warningColor = useThemeColor('warning')
    if (!isVisible) return null
    return (
        <View className="flex-row gap-2 items-start rounded-lg p-3 bg-warning-soft">
            <AlertTriangle size={16} color={warningColor} style={{ marginTop: 1 }} />
            <Text className="text-warning-soft-foreground flex-1" style={{ fontSize: 13 }}>
                This package is outside the @tinycld/ scope. Its code will run on your server with
                full access to your data — only install packages from authors you trust.
            </Text>
        </View>
    )
}
